import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import {
  checkForUpdate,
  startFlexibleUpdate,
  completeFlexibleUpdate,
} from '../services/appUpdate';
import { getGooglePlayUrl, openGooglePlay } from '../utils/pwaInstall';
import logger from '../utils/logger';

/**
 * Returns true when the app is running as a native Android build.
 */
function isAndroidNative() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

/**
 * Hook that checks Google Play for an available Android update and tracks
 * the update banner / in-app update flow.
 *
 * TODO(iOS): This currently only handles Android. A future iOS implementation
 * should check the App Store for a newer build and present an equivalent prompt.
 */
export function useAppUpdate() {
  const [state, setState] = useState({
    isChecking: false,
    updateAvailable: false,
    installedVersion: null,
    installedBuild: null,
    availableVersionCode: null,
    isUpdating: false,
    updateStarted: false,
    error: null,
  });
  const [dismissed, setDismissed] = useState(false);

  const check = useCallback(async () => {
    setState((s) => ({ ...s, isChecking: true, error: null }));

    try {
      const appInfo = await App.getInfo();
      const update = await checkForUpdate();

      setState((s) => ({
        ...s,
        isChecking: false,
        updateAvailable: update.updateAvailable,
        installedVersion: appInfo.version ?? null,
        installedBuild: appInfo.build ?? null,
        availableVersionCode: update.availableVersionCode,
      }));
    } catch (err) {
      logger.warn('useAppUpdate check failed:', err);
      setState((s) => ({ ...s, isChecking: false, error: err }));
    }
  }, []);

  useEffect(() => {
    if (!isAndroidNative()) {
      return undefined;
    }

    check();
    return undefined;
  }, [check]);

  const startUpdate = useCallback(async () => {
    setState((s) => ({ ...s, isUpdating: true, error: null }));

    try {
      await startFlexibleUpdate();
      setState((s) => ({ ...s, isUpdating: false, updateStarted: true }));
    } catch (err) {
      const playUrl = getGooglePlayUrl();
      if (playUrl) {
        openGooglePlay();
        setState((s) => ({ ...s, isUpdating: false, updateStarted: false }));
      } else {
        logger.error('In-app update failed and no Play Store URL is configured:', err);
        setState((s) => ({ ...s, isUpdating: false, error: err }));
      }
    }
  }, []);

  const completeUpdate = useCallback(async () => {
    if (!state.updateStarted) {
      return;
    }
    try {
      await completeFlexibleUpdate();
    } catch (err) {
      logger.warn('Failed to complete flexible update:', err);
    }
  }, [state.updateStarted]);

  useEffect(() => {
    if (!isAndroidNative()) {
      return undefined;
    }

    let listener;
    const setup = async () => {
      listener = await App.addListener('appStateChange', (appState) => {
        if (appState.isActive) {
          completeUpdate();
        }
      });
    };

    setup();

    return () => {
      if (listener && typeof listener.remove === 'function') {
        listener.remove();
      }
    };
  }, [completeUpdate]);

  const dismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  const showBanner =
    isAndroidNative() &&
    state.updateAvailable &&
    !dismissed &&
    !state.isUpdating &&
    !state.updateStarted;

  return {
    ...state,
    showBanner,
    dismiss,
    startUpdate,
  };
}
